const MAX_SOURCE_BYTES = 20 * 1024 * 1024
const TARGET_BYTES = 32 * 1024

const OUTPUT_CANDIDATES = [
  { maxDimension: 800, quality: 0.86 },
  { maxDimension: 720, quality: 0.78 },
  { maxDimension: 640, quality: 0.72 },
  { maxDimension: 512, quality: 0.72 },
  { maxDimension: 420, quality: 0.68 },
  { maxDimension: 320, quality: 0.64 },
  { maxDimension: 256, quality: 0.58 },
  { maxDimension: 192, quality: 0.52 },
]

const PROGRAM_OUTPUT_CANDIDATES = [
  { maxDimension: 1200, quality: 0.84 },
  { maxDimension: 960, quality: 0.80 },
  { maxDimension: 800, quality: 0.76 },
  { maxDimension: 640, quality: 0.72 },
  { maxDimension: 512, quality: 0.68 },
  { maxDimension: 420, quality: 0.64 },
  { maxDimension: 320, quality: 0.60 },
  { maxDimension: 256, quality: 0.56 },
  { maxDimension: 192, quality: 0.50 },
]

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file)
    const image = new Image()

    image.onload = () => resolve({ image, objectUrl })
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl)
      reject(new Error('Il file selezionato non è un’immagine leggibile.'))
    }
    image.src = objectUrl
  })
}

function canvasToBlob(canvas, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      blob => blob
        ? resolve(blob)
        : reject(new Error('Impossibile normalizzare questa immagine.')),
      'image/webp',
      quality
    )
  })
}

function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = () => reject(new Error('Impossibile leggere l’immagine normalizzata.'))
    reader.readAsDataURL(blob)
  })
}

function renderCandidate(image, sourceWidth, sourceHeight, maxDimension) {
  const scale = Math.min(1, maxDimension / Math.max(sourceWidth, sourceHeight))
  const width = Math.max(1, Math.round(sourceWidth * scale))
  const height = Math.max(1, Math.round(sourceHeight * scale))
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height

  const context = canvas.getContext('2d', { alpha: true })
  if (!context) throw new Error('Normalizzazione immagine non supportata dal browser.')

  context.drawImage(image, 0, 0, width, height)
  return { canvas, width, height }
}

/**
 * Decodifica e ricodifica le immagini prima dell'attuale upload Base64.
 * In questo modo metadata/chunk anomali non entrano nel payload e il
 * contratto API, i controller e lo storage rimangono invariati.
 */
export async function normalizeImageUpload(file, options = {}) {
  const targetBytes = options.targetBytes || TARGET_BYTES
  const outputCandidates = options.outputCandidates || OUTPUT_CANDIDATES

  if (!file || (file.type && !file.type.startsWith('image/'))) {
    throw new Error('Seleziona un file immagine valido.')
  }
  if (file.size > MAX_SOURCE_BYTES) {
    throw new Error('L’immagine supera il limite di 20 MB.')
  }

  const { image, objectUrl } = await loadImage(file)
  try {
    const sourceWidth = image.naturalWidth
    const sourceHeight = image.naturalHeight
    if (!sourceWidth || !sourceHeight) {
      throw new Error('L’immagine non contiene dimensioni valide.')
    }

    let normalized
    let outputWidth
    let outputHeight

    for (const candidate of outputCandidates) {
      const rendered = renderCandidate(
        image,
        sourceWidth,
        sourceHeight,
        candidate.maxDimension
      )
      normalized = await canvasToBlob(rendered.canvas, candidate.quality)
      outputWidth = rendered.width
      outputHeight = rendered.height
      if (normalized.size <= targetBytes) break
    }

    if (!normalized || normalized.size > targetBytes) {
      throw new Error('Impossibile ridurre questa immagine a una dimensione sicura.')
    }

    const dataUrl = await blobToDataUrl(normalized)
    return {
      dataUrl,
      originalBytes: file.size,
      outputBytes: normalized.size,
      width: outputWidth,
      height: outputHeight,
    }
  } finally {
    URL.revokeObjectURL(objectUrl)
  }
}

export const normalizePersonImage = file => normalizeImageUpload(file)

export const normalizeProgramImage = file => normalizeImageUpload(file, {
  targetBytes: TARGET_BYTES,
  outputCandidates: PROGRAM_OUTPUT_CANDIDATES,
})

export function formatImageBytes(bytes) {
  if (bytes < 1024) return `${bytes} B`
  return `${Math.round(bytes / 1024)} KB`
}
